import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { I18nProvider as LinguiProvider } from '@lingui/react'
import { graphql, useRelayEnvironment } from 'react-relay'
import { commitMutation } from 'relay-runtime'
import type { I18nProviderUpdateLocaleMutation } from './__generated__/I18nProviderUpdateLocaleMutation.graphql'
import { i18n, loadSharedCatalog } from './i18n'
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  normalizeLocale,
  type SupportedLocale,
} from './locales'
import { ServerContext } from '../ServerContext'
import { useAuthContext } from '../useAuthContext'

const UpdateLocaleMutation = graphql`
  mutation I18nProviderUpdateLocaleMutation($input: UpdateMyLocaleInput!) {
    updateMyLocale(input: $input) {
      user {
        id
        locale
      }
    }
  }
`

const LOCALE_STORAGE_KEY = 'guild-locale'

const LocaleContext = createContext<{
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
}>({
  locale: DEFAULT_LOCALE,
  setLocale: () => undefined,
})

export const useLocale = () => useContext(LocaleContext)

const detectBrowserLocale = (): SupportedLocale => {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE

  for (const lang of navigator.languages ?? [navigator.language]) {
    // Case-insensitive match (e.g. "pt-br" → "pt-BR")
    const normalized = normalizeLocale(lang)
    if (normalized) return normalized
    // Try language-only (e.g. "fr" from "fr-FR")
    const base = lang.split('-')[0]
    const normalizedBase = normalizeLocale(base)
    if (normalizedBase) return normalizedBase
  }

  return DEFAULT_LOCALE
}

const getInitialLocale = (serverLocale?: string): SupportedLocale => {
  // During SSR, use server-detected locale
  if (serverLocale && isSupportedLocale(serverLocale)) return serverLocale

  if (typeof window === 'undefined') return DEFAULT_LOCALE
  // Explicit user preference (localStorage) takes priority
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && isSupportedLocale(stored)) return stored
  } catch {
    // Ignore storage access errors and fall back to browser detection.
  }
  // No explicit preference — detect from browser languages
  return detectBrowserLocale()
}

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const serverContext = useContext(ServerContext)
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    getInitialLocale(serverContext?.locale)
  )
  const { accessToken } = useAuthContext()
  const relayEnvironment = useRelayEnvironment()

  // During SSR, use the per-request i18n instance from ServerContext to avoid
  // race conditions between concurrent requests sharing the module-level singleton.
  // On the client, fall back to the shared singleton.
  const activeI18n = serverContext?.i18n ?? i18n

  useEffect(() => {
    if (locale === DEFAULT_LOCALE) return
    // If SSR already activated this locale (with a real catalog), skip the
    // redundant empty-load + activate to avoid an unnecessary re-render.
    if (i18n.locale === locale) {
      // Still load the shared catalog async in case the SSR catalog was
      // partial (e.g. only shared, not screen-level)
      void loadSharedCatalog(locale)
      return
    }
    // Register empty catalog so activate() doesn't warn, then load real one async
    i18n.load(locale, {})
    i18n.activate(locale)
    void loadSharedCatalog(locale)
  }, [])

  const setLocale = useCallback(
    (newLocale: SupportedLocale) => {
      // Switch immediately — register empty catalog so activate() doesn't warn,
      // then load the real catalog async in the background
      i18n.load(newLocale, {})
      i18n.activate(newLocale)
      setLocaleState(newLocale)
      void loadSharedCatalog(newLocale)

      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale)
      } catch {
        // Ignore storage access errors; locale still updates for this session.
      }
      try {
        const secure = window.location.protocol === 'https:' ? ';Secure' : ''
        document.cookie = `guild-locale=${newLocale};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax${secure}`
      } catch {
        // Ignore cookie write failures.
      }
      try {
        document.documentElement.lang = newLocale
      } catch {
        // Ignore DOM write failures in non-browser contexts.
      }

      // Persist to database for authenticated users (fire-and-forget)
      if (accessToken) {
        commitMutation<I18nProviderUpdateLocaleMutation>(relayEnvironment, {
          mutation: UpdateLocaleMutation,
          variables: { input: { locale: newLocale } },
        })
      }
    },
    [accessToken, relayEnvironment]
  )

  const contextValue = useMemo(
    () => ({ locale, setLocale }),
    [locale, setLocale]
  )

  return (
    <LocaleContext.Provider value={contextValue}>
      <LinguiProvider i18n={activeI18n}>{children}</LinguiProvider>
    </LocaleContext.Provider>
  )
}
